'use client'

import React, { useState } from 'react'
import { LuArrowUpLeft, LuArrowUpRight } from 'react-icons/lu'
import { toast } from 'sonner'

import { getLocalizedTranslationData } from '@/utilities/translationsData'

import { useLocale } from '../LocaleContext'

export type DownloadECatalogButtonProps = {
  href?: string | null
  download?: string | boolean
  children?: React.ReactNode
  className?: string
}

const buttonBase =
  'download-ecatalog-btn inline-flex min-h-11 min-w-[220px] cursor-pointer items-center justify-center gap-3 rounded-none px-6 py-[0.85rem] text-left font-bebas-neue text-[25px] font-bold uppercase leading-[26px] tracking-normal no-underline disabled:cursor-not-allowed disabled:opacity-70'
const buttonOutline =
  'h-[113.1px] w-[287.11px] border-2 border-[#e00000] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gallium-white'

export function DownloadECatalogButton({
  href,
  download = 'E-Catalogue.pdf',
  children,
  className = '',
}: DownloadECatalogButtonProps) {
  const { locale, translatedData } = useLocale()
  const [loading, setLoading] = useState(false)
  const label = translatedData?.common.download_ecatalogue ?? 'Download E-Catalogue'
  const catalogueNotAvailableMessage =
    (translatedData?.common as { catalogue_not_available?: string } | undefined)
      ?.catalogue_not_available ??
    (
      getLocalizedTranslationData((locale as AppLocale) ?? 'en').common as
        | { catalogue_not_available?: string }
        | undefined
    )?.catalogue_not_available ??
    'E-Catalogue Not available'
  const [firstWord, ...restWords] = label.split(' ')
  const secondLine = restWords.join(' ')
  const resolvedChildren = children ?? (
    <div
      dir={locale == 'ar' ? 'rtl' : 'ltr'}
      className="flex flex-row items-center justify-start gap-2.5 pl-2.5 pr-20 max-[400px]:pt-4 pt-8 text-left text-[25px] max-[400px]:text-xs font-light leading-[26px] uppercase rtl:justify-end rtl:pr-2.5 rtl:pl-10"
    >
      <div className="flex pl-5 rtl:pr-5 rtl:pl-[unset] flex-col items-start rtl:items-end rtl:w-full rtl:ml-3">
        <span className=" whitespace-nowrap">{firstWord}</span>
        <span className=" whitespace-nowrap">{secondLine}</span>
      </div>
      {locale == 'ar' ? (
        <LuArrowUpLeft size={18} aria-hidden className="shrink-0" />
      ) : (
        <LuArrowUpRight
          size={18}
          aria-hidden
          className="shrink-0 translate-x-[35px] translate-y-[10px]"
        />
      )}
    </div>
  )

  const handleClick = async () => {
    if (loading) return

    if (!href?.trim()) {
      toast.error(catalogueNotAvailableMessage)
      return
    }

    setLoading(true)
    try {
      const res = await fetch(href)
      if (!res.ok) {
        toast.error(catalogueNotAvailableMessage)
        return
      }

      const blob = await res.blob()
      const filename =
        typeof download === 'string' ? download : download ? 'E-Catalogue.pdf' : 'E-Catalogue.pdf'
      const url = URL.createObjectURL(
        blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' }),
      )
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      anchor.rel = 'noopener'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error(catalogueNotAvailableMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      aria-busy={loading}
      className={`${buttonBase} ${buttonOutline} w-[287.11px] min-w-[220px]  ${className}`.trim()}
    >
      {resolvedChildren}
    </button>
  )
}
